var React = require('react');
var { connect } = require('react-redux');
var GraphToggle = require('GraphToggle');
const { useTranslation, withTranslation } = require('react-i18next');
var SensorGraph = require('SensorGraph');
const { useEffect, useState, useMemo } = require('react');
var GeneralClasses = require('GeneralClasses');
var { unitDisplayName } = require('unitsManager');
var dayjs = require('../../utils/dayjsConfig');
require('./AllFarmView/FieldDetailsWPGraph.css');

const DATE_FILTER_OPTIONS = [
  { days: 1, key: 'last_day' },
  { days: 7, key: 'last_week' },
  { days: 14, key: 'last_two_weeks' },
  { days: 30, key: 'last_month' },
  { days: 182, key: 'last_six_months' },
  { days: 365, key: 'last_year' },
  { days: 0, key: 'max' },
];

const SoilTemperatureGraphContainer = (props) => {
  const { t } = useTranslation();
  const [sensor_graph, setSensor_graph] = useState(null);
  const [daysBack, setDaysBack] = useState(7);

  let userUnits =
    props.userData && 'temperature' in props.userData
      ? unitDisplayName(props.userData.temperature)
      : '';

  let dataInLoadingState =
    props.selectedField?.historical_data?.soil_temp?.values;

  let graphTitleColor =
    props.field_state === 'active' ? 'text-grey45' : 'text-grey71';

  const dateFilterNames = useMemo(
    () =>
      DATE_FILTER_OPTIONS.reduce(
        (acc, opt) => ({
          ...acc,
          [opt.days]: t(opt.key),
        }),
        {},
      ),
    [t],
  );

  // Load data and reload graph when dependencies change
  useEffect(() => {
    if (
      !props?.selectedField?.historical_data?.soil_temp?.values ||
      Object.keys(props.selectedField.historical_data.soil_temp.values)
        .length === 0
    ) {
      return;
    }

    const samples = loadSoilTempData(daysBack);
    if (sensor_graph && samples.length > 0) {
      reloadGraph(samples);
    }
  }, [
    props.selectedField?.historical_data?.soil_temp?.values,
    props.userData?.temperature,
    sensor_graph,
    daysBack,
  ]);

  const loadSoilTempData = (days) => {
    const soilTempValues = props.selectedField.historical_data.soil_temp.values;
    const timezone = props.selectedField?.geo_data?.time_zone;
    const keys = Object.keys(soilTempValues);
    const sortedTimestamps = [...keys].sort(
      (a, b) => parseInt(a) - parseInt(b),
    );

    // Calculate cutoff timestamp based on days back (0 means no filtering - show all)
    // Use field's timezone for accurate date calculations
    const cutoffTimestamp =
      days > 0 ? dayjs().tz(timezone).subtract(days, 'day').unix() : 0;

    const samples = [];
    for (let timestamp of sortedTimestamps) {
      // Filter by days back (skip filtering if days is 0 - max option)
      if (days > 0 && parseInt(timestamp) < cutoffTimestamp) {
        continue;
      }

      const rawValue = soilTempValues[timestamp];

      if (
        rawValue === null ||
        rawValue === undefined ||
        isNaN(parseFloat(rawValue))
      ) {
        continue;
      }

      samples.push({
        date: timezone
          ? dayjs.unix(parseInt(timestamp)).tz(timezone)
          : dayjs.unix(parseInt(timestamp)),
        value: parseFloat(rawValue),
        index: samples.length,
      });
    }

    if (days > 182 && samples.length > 1) {
      return samples.filter(function (sample, index) {
        return index % 2 === 0;
      });
    }

    return samples;
  };

  const handleTimeRangeChange = (e, days) => {
    e.preventDefault();
    // Don't stopPropagation — Bootstrap relies on the click bubbling up
    // to auto-close the dropdown menu after selection.
    setDaysBack(days);
    mixpanel.track('Soil Temperature Timeframe Selection Changed', {
      'Days Back': days,
    });
  };

  const reloadGraph = (samples) => {
    if (!samples || samples.length === 0 || !sensor_graph) {
      return;
    }

    const dataToDisplay = {
      soil_temp: new GeneralClasses.Sensor(
        {
          id: t('soil_temperature'),
          name: t('soil_temperature'),
          displayName: t('soil_temperature'),
          data: samples,
          color: '#20a2cf',
          coordinates: {
            latitude: props.selectedField?.geo_data?.latitude || '0',
            longitude: props.selectedField?.geo_data?.longitude || '0',
          },
          settings: { is_active: true },
        },
        t('soil_temperature'),
      ),
    };

    const timezone = props.selectedField?.geo_data?.time_zone;
    const historicalData = props.selectedField?.historical_data;

    sensor_graph.loadData(
      dataToDisplay,
      historicalData,
      undefined,
      timezone,
      {},
      {},
    );

    // Set Y-axis bounds based on temperature data
    if (sensor_graph.current_chart) {
      const values = samples.map((s) => s.value);
      const minVal = Math.min(...values);
      const maxVal = Math.max(...values);
      const padding = (maxVal - minVal) * 0.1 || 5;

      sensor_graph.current_chart.options.scales.y.min = minVal - padding;
      sensor_graph.current_chart.options.scales.y.max = maxVal + padding;
      delete sensor_graph.current_chart.options.scales.y.suggestedMin;
      delete sensor_graph.current_chart.options.scales.y.suggestedMax;
      sensor_graph.current_chart.options.scales.y.beginAtZero = false;
      sensor_graph.current_chart.update();
    }
  };

  return (
    <div className='field-details-wp field-details-wp--fluid field-details-wp--temperature'>
      <div className='field-details-wp__header'>
        <div className='field-details-wp__title-group'>
          <div className='field-details-wp__toggle'>
            <GraphToggle
              initialValue='temperature'
              onToggleClick={(toggle) => props.onToggleClick(toggle)}
              icon={props.icon}
              temperatureUnit={props.temperatureUnit}
            />
          </div>
          <span
            className={'field-details-wp__title ' + graphTitleColor}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {t('soil_temperature_graph_title')}
            {!dataInLoadingState && (
              <span
                className='spinner-border text-grey45'
                role='status'
                style={{ width: '15px', height: '15px' }}
              />
            )}
          </span>
        </div>

        {props.field_state === 'active' && (
          <div
            className='dropdown text-size-16 text-grey71 align-self-center'
            // Match the title's translate so the dropdown sits at the
            // same vertical position; raise above the chart frame so
            // the open menu isn't clipped.
            style={{
              transform: 'translateY(8px)',
              position: 'relative',
              zIndex: 10,
            }}
          >
            <div
              className='dropdown-toggle'
              data-toggle='dropdown'
              aria-haspopup='true'
              aria-expanded='false'
              style={{ cursor: 'pointer' }}
            >
              {dateFilterNames[daysBack.toString()]}
            </div>

            <div
              className='dropdown-menu dropdown-menu-right'
              aria-labelledby='dropdownMenuButton'
            >
              {DATE_FILTER_OPTIONS.map(({ days }) => (
                <a
                  key={`days-${days}`}
                  className='dropdown-item text-grey71'
                  href='#'
                  onClick={(e) => handleTimeRangeChange(e, days)}
                >
                  {dateFilterNames[days.toString()]}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className='field-details-wp__frame'>
        <div className='field-details-wp__chart'>
          <SensorGraph
            onRef={(ref) => setSensor_graph(ref)}
            hideNavigationButtons={true}
            units={userUnits}
            isLoading={false}
            shouldDrawBottom={false}
            shouldDrawInputs={false}
            shouldDrawResetButton={true}
            toolTipMode={'index'}
            distributionMode={'linear'}
            shouldDisplayZoom={false}
            shouldSpaceData={true}
            shouldDiplayUTC={false}
            shouldScaleGraph={false}
            field_state={props.field_state}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = function (state) {
  return {
    selectedField: state.distribution_data.selected_entities.field,
    selectedFarmingUnit: state.distribution_data.selected_entities.farming_unit,
    selectedDistribution:
      state.distribution_data.selected_entities.distribution,
    loadHistoryStatus: state.distribution_data.field_load_history,
    userData: state.authentication_data.user_data,
    field_state: state.distribution_data.field_state,
  };
};
const mapDispatchToProps = {};

module.exports = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(SoilTemperatureGraphContainer));
